import { IQuery } from 'core-ts-types';
import * as _4 from 'lodash4';
import { ORM } from 'utility-ts-orm';

import { IStudyGroupCoursesDatasource } from '../datasources/study_group_courses_datasource';
import { IStudyGroupSectionsDatasource } from '../datasources/study_group_sections_datasource';
import { IStudyGroupTermsDatasource } from '../datasources/study_group_terms_datasource';
import {
  ICourseSection,
  ISectionData,
  IStudyGroupCourse,
  IStudyGroupSection,
  IStudyGroupTerm
} from '../study_group_interfaces';

export class StudyGroupsQuery implements IQuery {
  private _result: {
    terms: ISectionData[];
    hasTerms: boolean;
    allSections: ICourseSection[];
    hasNotParticipated: boolean;
  };

  constructor(
    private ngStudyGroupCoursesDatasource: IStudyGroupCoursesDatasource,
    private ngStudyGroupSectionsDatasource: IStudyGroupSectionsDatasource,
    private ngStudyGroupTermsDatasource: IStudyGroupTermsDatasource
  ) {
    ORM.registerQuery(this, ngStudyGroupCoursesDatasource, ngStudyGroupSectionsDatasource, ngStudyGroupTermsDatasource);
  }

  run() {
    const terms = this.constructSectionsData();
    const hasTerms = terms.length > 0;
    const allSections = this.composeSections();
    const hasNotParticipated = allSections.length > 0 && this.hasNotParticipated(allSections);
    this._result = { terms, hasTerms, allSections, hasNotParticipated };
    return this;
  }

  constructSectionsData(): ISectionData[] {
    // combine allsections(section with courses) data with terms data.
    return this.constructTermsSections(this.composeSections());
  }

  hasNotParticipated(sections: ICourseSection[]) {
    return sections && !_4.some(sections, 'is_buddy');
  }

  composeSections() {
    // Remove any sections that doesn't have associated course
    const sections = _4.filter(
      this.ngStudyGroupSectionsDatasource.read(),
      (section) => !_4.isEmpty(this.getCourseForSection(section))
    );

    // combine courses data with sections data.
    return _4.map(sections, (section: IStudyGroupSection) => {
      const course = this.getCourseForSection(section);
      return {
        courseNk: course.cd,
        courseId: course.id,
        courseTitle: course.title,
        ...section
      };
    });
  }

  constructTermsSections(courseSections: any): ISectionData[] {
    const terms = this.ngStudyGroupTermsDatasource.read();

    // terms(from API) have courses data. so we are mapping courses data with sections data (which has courses inside).
    return _4.map(terms, (term: IStudyGroupTerm) => {
      const courseIds = term.courses;
      const sectionsAssociatedWithCourse = _4.flatMap(courseIds, (courseID: number) =>
        _4.get(this.ngStudyGroupCoursesDatasource.get(courseID)!, 'sections', [])
      );
      const sections = _4.compact(
        _4.map(sectionsAssociatedWithCourse, (sectionId: number) =>
          _4.find(
            courseSections,
            (coursesection: any) => _4.isEqual(coursesection.id, sectionId) && _4.isEqual(coursesection.term, term.id)
          )
        )
      );

      return {
        termName: term.name,
        sections
      };
    });
  }

  getCourseForSection(section: IStudyGroupSection): IStudyGroupCourse {
    return this.ngStudyGroupCoursesDatasource.get(section.course) as IStudyGroupCourse;
  }

  get result() {
    return this._result;
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-study-groups/src/queries/study_groups_query.ts